/**
 * The ONE emit seam for the generic, delegated sidebar-refresh mechanism.
 *
 * Design (Jake, 2026-07-14): a mutation in a given tab fires ONE general
 * event carrying the tab's summary-store KEY; the sidebar owner that renders
 * that key listens for the single event and refetches. This replaces the
 * earlier per-domain event vocabulary (``client.mutated``, ``store.mutated``,
 * ...) with one delegated event -- DRY, no bespoke event type per tab.
 *
 *   event type: ``sidebar:refresh``
 *   payload:    { id, tenantId, ...extra }
 *     - ``id``       -- the summary-store KEY the sidebar row maps to
 *                       (``clients``, ``decoration_library``, ``stores``, ...);
 *                       these are exactly the field names in the per-layout
 *                       dashboard-summary payload. No new registry: the key
 *                       IS the relevance token a listener matches on.
 *     - ``tenantId`` -- ``effectiveTenantId()``, so a summary subscriber can
 *                       drop an event for a tenant it is no longer showing
 *                       (stale-tenant guard, unchanged from the per-domain era).
 *
 * A tab's write action calls ``emitSidebarRefresh('<summary-key>')`` on a
 * successful mutation. That single line is the ENTIRE per-tab cost.
 *
 * The bus itself (eventBus.js) stays a generic ``Map<type, Set<fn>>``; this
 * module owns the sidebar-refresh CONVENTION (the event name + payload shape +
 * tenant derivation) so both the emit sites and the listen sites agree on one
 * contract from one place.
 */
import { useEventBus } from '@/stores/eventBus'
import { effectiveTenantId } from '@/stores/effectiveTenant'

/**
 * The single event type every sidebar-refresh emit + subscribe uses.
 * Exported so listeners subscribe to the same constant rather than a
 * stringly-typed literal.
 */
export const SIDEBAR_REFRESH_EVENT = 'sidebar:refresh'

/**
 * Announce that the summary counter keyed by ``id`` may have changed.
 *
 * @param {string} id  The summary-store key the mutated tab maps to.
 * @param {object} [extra]  Additional payload fields (merged after
 *   ``id``/``tenantId``, but ``id`` and ``tenantId`` are always set by this
 *   function -- a caller cannot accidentally spoof them).
 */
export function emitSidebarRefresh(id, extra = {}) {
    useEventBus().emit(SIDEBAR_REFRESH_EVENT, {
        ...extra,
        id,
        tenantId: effectiveTenantId(),
    })
}

/**
 * Announce that a store was CREATED, so the distributor sidebar's STORES
 * counter ("N stores . M live") refetches.
 *
 * Interim seam: unlike clients / decoration_library, there is no Pinia store
 * for stores/campaigns -- the store-create write is a jsonapi-vuex Vuex
 * dispatch inside StoreAddEdit.vue's ``saveCampaign`` method. Rather than bury
 * a bare ``emitSidebarRefresh('stores')`` in that 300-line method, this thin
 * named function owns the emit: it documents intent at the call site and gives
 * ONE place to move the announcement when ``stores`` gets a real store later.
 * Full store-ification of the store-editor writes is tracked in
 * https://trello.com/c/g9K52FsA.
 *
 * Only the CREATE path calls this -- editing an existing store (autosave /
 * update_campaign RPC) does not change the COUNT, so it must NOT emit.
 */
export function notifyStoreCreated() {
    emitSidebarRefresh('stores')
}

/**
 * Announce that a store was DELETED, so the distributor sidebar's STORES
 * counter ("N stores . M live") refetches.
 *
 * Deleting a store DROPS a row, so it moves the count exactly as much as a
 * create does -- it emits the SAME ``stores`` key as notifyStoreCreated. A
 * delete-specific name (rather than reusing notifyStoreCreated) documents the
 * mutation intent at the call site: the delete lives in StoreList.vue's
 * ``deleteCampaign`` method, far from the create in StoreAddEdit's saveCampaign.
 *
 * Only a SUCCESSFUL delete calls this -- the ``deleteCampaign`` GET has no
 * catch, so a failed request throws out before reaching this line and nothing
 * is announced (a failed delete removed no row).
 *
 * Interim seam mirroring notifyStoreCreated: there is no Pinia store for
 * stores/campaigns, so this thin named function owns the emit and gives ONE
 * place to move the announcement when ``stores`` gets a real store later.
 * Tracked in https://trello.com/c/g9K52FsA.
 */
export function notifyStoreDeleted() {
    emitSidebarRefresh('stores')
}

/**
 * Announce that a store was ARCHIVED or UNARCHIVED, so the distributor
 * sidebar's STORES counter refetches. Archived stores are left out of that
 * count (it agrees with the Stores list), so both directions move it --
 * the same ``stores`` key as a create or delete. Success-only, like
 * notifyStoreDeleted.
 */
export function notifyStoreArchiveChanged() {
    emitSidebarRefresh('stores')
}

/**
 * Announce that team membership changed, so the distributor sidebar's TEAM
 * counter ("N owners . M builders") refetches.
 *
 * The team summary is a COUNT BY ROLE (derive_team_summary in
 * api/src/endpoints/distributor_summary.py), so a ROLE CHANGE moves the tally
 * (Owner -> Builder) just as much as an add or remove does. Callers therefore
 * emit on ANY team-membership mutation -- change-role, edit-member, remove,
 * transfer-ownership, invite -- rather than trying to predict which mutation
 * actually moved the count. A redundant refetch when the tally did not change
 * is harmless: the summary just returns the same value.
 *
 * Emit ONLY on a successful mutation (after the await resolves, never in the
 * catch): a failed write changed nothing.
 *
 * Interim seam: there is no Pinia store for the team mutations -- they live in
 * scattered composables (useTeamMembers, useTeamSubscription) and the invite
 * dialog. Full store-ification of the team writes is tracked in
 * https://trello.com/c/g9K52FsA; this thin named function gives ONE place to
 * move the announcement when that lands.
 */
export function notifyTeamChanged() {
    emitSidebarRefresh('team')
}

/**
 * Announce that a distributor's sales-tax nexus states were saved, so the
 * distributor sidebar's TAX NEXUS counter ("N states") refetches.
 *
 * The tax_nexus summary key is the count of states in
 * ``distributors.nexus_states`` (derive_tax_nexus_summary). Saving the nexus
 * editor changes that count, so the counter must refetch. Like the store seam,
 * the mutation site (TaxNexusEditor's save) is a plain service call, not a
 * Pinia store action, so this thin named function owns the emit -- documenting
 * intent at the call site and giving ONE place to move the announcement if
 * tax_nexus gets a real store later. Tracked in https://trello.com/c/g9K52FsA.
 *
 * Only the SUCCESS path of a save calls this -- a failed save does not change
 * the persisted count, so it must NOT emit.
 */
export function notifyTaxNexusChanged() {
    emitSidebarRefresh('tax_nexus')
}

/**
 * Announce that the distributor's DEFAULT pricing changed, so the
 * distributor sidebar's PRICING row (default-margin sub-text, keyed on the
 * ``pricing`` summary field) refetches.
 *
 * Interim seam mirroring notifyStoreCreated: the default-margin write lives
 * in DefaultsPricing.vue's ``save`` method (a bare axios PATCH, not a Pinia
 * store), so this thin named function owns the emit -- it documents intent at
 * the call site and gives ONE place to move the announcement if the pricing
 * defaults get a real store later. Full store-ification of these writes is
 * tracked in https://trello.com/c/g9K52FsA.
 *
 * Only a SUCCESSFUL save calls this -- a failed PATCH leaves the stored value
 * (and therefore the counter) unchanged, so it must NOT emit. Reset-to-fallback
 * (clear) delegates to save() and inherits this emit through that single path.
 */
export function notifyPricingDefaultsChanged() {
    emitSidebarRefresh('pricing')
}

/**
 * Announce that the distributor's SUBSCRIPTION STATE changed, so the
 * distributor sidebar's PAYMENTS row (keyed on the ``payments`` summary field)
 * refetches.
 *
 * The payments summary (derive_payments_summary,
 * api/src/services/distributor_summary.py) reads the subscription STATE,
 * ``trial_ends_at``, and ``plan_name``, and renders "``plan`` . N days trial
 * left" (or "Trial ends today" in the last 24 hours) plus a green/amber/red status. Emit ONLY for mutations that move one of
 * those three -- renew (state + trial) and cancel (state). Callers therefore
 * emit on the SUCCESS path of renewSubscription + cancelSubscription.
 *
 * Do NOT emit for mutations that leave state/trial/plan unchanged:
 *   - payment-METHOD-only edits (a new card) -- the summary shows plan/trial/
 *     state, never the card, so a card update does not move the row;
 *   - the plan-change request (PlanChangeModal) -- it logs a sales request and
 *     does NOT change the persisted plan, so plan_name/state are unchanged;
 *   - team mutations (delete / invite / transfer teammate) -- those move the
 *     ``team`` count, not payments (handled by notifyTeamChanged, PR-C).
 *
 * Only a SUCCESSFUL mutation calls this -- a failed renew/cancel leaves the
 * stored state (and therefore the row) unchanged, so it must NOT emit.
 *
 * Interim seam mirroring the other named seams: the subscription writes are
 * scattered plain axios POSTs (RenewSubscriptionDialog, DistributorAddEdit,
 * useTeamSubscription), not a Pinia store, so this thin named function owns the
 * emit and gives ONE place to move the announcement when the subscription
 * writes get a real store later. Tracked in https://trello.com/c/g9K52FsA.
 */
export function notifyPaymentsChanged() {
    emitSidebarRefresh('payments')
}
